import { Box, makeStyles } from "@material-ui/core";
import * as React from "react";

import { PropertyEditLayout as BaseTemplate } from "@/components/property_edit/PropertyEditLayout";
import { getFormatCost, getFormatStartMonth, getLatestMonthlyCashflowResultByCostType } from "@/utilities/propertyEdit";
import { PropertyHolder } from "@/view_models/atoms";

import { LatestHistoryContent } from "../../EditPropertyHistory";

const useStyles = makeStyles(() => ({
  content: {
    maxWidth: 640,
    margin: "0 auto",
  },
}));

interface EditPropertySectionManagementBuildingCostProps {
  propertyHolder: PropertyHolder;
  onClickPageBack: () => void;
}

import { BuildingBasicCostEditor } from "./BuildingBasicCostEditor";
import { EditPropertySectionManagementBuildingCostHistory } from "./History";
import { ManagementBuildingCostType } from "./types";

const EditPropertySectionManagementBuildingCost: React.FC<EditPropertySectionManagementBuildingCostProps> = ({
  propertyHolder,
  onClickPageBack,
}) => {
  const classes = useStyles();

  // 過去履歴の表示・非表示
  const [isShowHistory, setIsShowHistory] = React.useState(false);
  // 過去履歴ページに渡す情報
  const [selectedCostType, setSelectedCostType] = React.useState<ManagementBuildingCostType | null>(null);

  /*
  　その他一過性の支出系コストの取得（履歴形式）
  */
  // property_tax を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultPropertyTax = React.useMemo(() => {
    return getLatestMonthlyCashflowResultByCostType(
      propertyHolder.user_input_monthly_cashflow_results,
      "固定資産税・都市計画税"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const propertyTax = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultPropertyTax?.property_tax),
    [latestMonthlyCashflowResultPropertyTax]
  );

  // supplies_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultSuppliesCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResultByCostType(propertyHolder.user_input_monthly_cashflow_results, "消耗品費");
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const suppliesCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultSuppliesCost?.supplies_cost),
    [latestMonthlyCashflowResultSuppliesCost]
  );

  // large_scale_repair_fund_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultLargeScaleRepairFundCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResultByCostType(
      propertyHolder.user_input_monthly_cashflow_results,
      "設備修繕、大規模修繕費用"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const largeScaleRepairFundCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultLargeScaleRepairFundCost?.large_scale_repair_fund_cost),
    [latestMonthlyCashflowResultLargeScaleRepairFundCost]
  );

  // misc_onetime_cost を含む最新のMonthlyCashflowResultを取得
  const latestMonthlyCashflowResultMiscOnetimeCost = React.useMemo(() => {
    return getLatestMonthlyCashflowResultByCostType(
      propertyHolder.user_input_monthly_cashflow_results,
      "その他一過性の支出"
    );
  }, [propertyHolder.user_input_monthly_cashflow_results]);
  const miscOnetimeCost = React.useMemo(
    () => getFormatCost(latestMonthlyCashflowResultMiscOnetimeCost?.misc_onetime_cost),
    [latestMonthlyCashflowResultMiscOnetimeCost]
  );

  const onClickPageBackFromHistory = () => {
    setIsShowHistory(false);
  };
  if (isShowHistory) {
    if (selectedCostType === "基本経費情報") {
      return (
        <BaseTemplate headerTitle="基本経費情報編集" onBack={onClickPageBackFromHistory} isBackGroundWhite={true}>
          <Box className={classes.content}>
            <BuildingBasicCostEditor />
          </Box>
        </BaseTemplate>
      );
    }
    return (
      <EditPropertySectionManagementBuildingCostHistory
        costType={selectedCostType}
        onClickPageBack={onClickPageBackFromHistory}
      />
    );
  }

  const handleClickShowMoreHistory = (costType: ManagementBuildingCostType) => {
    setSelectedCostType(costType);
    setIsShowHistory(true);
  };

  return (
    <BaseTemplate headerTitle="一棟物件経費情報" onBack={onClickPageBack} isBackGroundWhite={true}>
      <Box>
        <Box className={classes.content}>
          <LatestHistoryContent
            title=""
            contents={[
              {
                label: "管理会社委託費（建物＋賃貸管理含む）",
                value: propertyHolder.property.building_detail?.management_company_commission
                  ? `${propertyHolder.property.building_detail.management_company_commission.toLocaleString()}円`
                  : "-円",
              },
              {
                label: "共用部水道光熱費",
                value: propertyHolder.property.building_detail?.shared_utility_expenses
                  ? `${propertyHolder.property.building_detail.shared_utility_expenses.toLocaleString()}円`
                  : "-円",
              },
              {
                label: "その他経費",
                value: propertyHolder.property.building_detail?.misc_building_cost
                  ? `${propertyHolder.property.building_detail.misc_building_cost.toLocaleString()}円`
                  : "-円",
              },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("基本経費情報")}
          />

          {/* 固定資産税・都市計画税 */}
          <LatestHistoryContent
            title="固定資産税・都市計画税"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultPropertyTax) },
              { label: "金額", value: propertyTax },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("固定資産税・都市計画税")}
          />

          {/* 設備修繕、大規模修繕費用 */}
          <LatestHistoryContent
            title="設備修繕、大規模修繕費用"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultLargeScaleRepairFundCost) },
              { label: "金額", value: largeScaleRepairFundCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("設備修繕、大規模修繕費用")}
          />

          {/* 消耗品費 */}
          <LatestHistoryContent
            title="消耗品費"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultSuppliesCost) },
              { label: "金額", value: suppliesCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("消耗品費")}
          />

          {/* 一過性の支出 */}
          <LatestHistoryContent
            title="その他一過性の支出"
            contents={[
              { label: "支払開始月", value: getFormatStartMonth(latestMonthlyCashflowResultMiscOnetimeCost) },
              { label: "金額", value: miscOnetimeCost },
            ]}
            onClickShowMoreHistory={() => handleClickShowMoreHistory("その他一過性の支出")}
          />
        </Box>
      </Box>
    </BaseTemplate>
  );
};

export default EditPropertySectionManagementBuildingCost;
